import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ColumnDefinition,
  getLabelValueColumn,
  renderColumn,
  sortableColumn
} from 'components/TableColumn/TableColumn';
import {
  CampaignState,
  RtbCampaignPlanType,
  RtbLegacyOptimize
} from 'core/rtbCampaign/RtbCampaign';
import formatters from './listFormatters';
import styles from './campaignDashboard.module.scss';
import { useCallAPI } from 'hooks/useCallAPI';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager
} from 'core/rtbCampaign/RtbCampaignManager';
import {
  CampaignDashboardManager,
  DefaultCampaignDashboardManager
} from 'core/campaignDashboard/CampaignDashboardManager';
import i18n from 'i18n';
import { useCoreContext } from 'contexts/coreContext';
import { camelCase, defaultTo, floor, get, isNumber, values } from 'lodash';
import {
  formatPriceWithCurrency,
  getCurrencyLabel
} from 'helper/CurrencyHelper';
import { addOnEnabled } from 'core/permission/PermissionDSL';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import moment from 'moment';
import {
  AdTypeOptionItem,
  AdTypeOptionType,
  CampaignDashboardRecord
} from 'core/campaignDashboard/CampaignDashboard';
import {
  AdjustmentTagType,
  allAdjustmentTagTypes
} from 'core/campaignDashboard/AdjustmentTagType';
import { BidStrategy, L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import { divideValue } from 'components/ReportTable/ReportDataHelper';
import { numberWithCommas } from 'utils/StringUtil';
import {
  getCampaignProgressRate,
  getDailyBudgetCampaignProgressRate
} from 'utils/CampaignUtil';
import { renderBidPriceInfo } from 'components/CampaignBidPriceInfo/CampaignBidPriceInfo';

export enum CampaignDashboardColumns {
  ID = 'campaignId',
  AGENCY = 'agencyName',
  ADVERTISER = 'advertiserName',
  ORDER = 'orderName',
  PROGRESS = 'executeRate',
  CTR = 'ctr',
  OPTIMIZE = 'optimize',
  ADJUSTMENT_TAGS = 'adjustmentTags',
  WEIGHT = 'magnificationRatio',
  START_DATE = 'startDate',
  END_DATE = 'endDate'
}

export enum CampaignDashboardExcelColumns {
  ORDER_ID = 'orderId',
  CAMPAIGN_ID = 'campaignId',
  CAMPAIGN_NAME = 'campaignName',
  ADTYPE = 'adType',
  AGENCY = 'agencyName',
  BUDGET = 'budget',
  SPENT = 'spent',
  OPTIMIZE = 'optimize',
  BID_PRICE = 'bidPrice',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  EDITBTNS = 'editBtns'
}

const defaultCampaignManager: RtbCampaignManager =
  new DefaultRtbCampaignManager();

/**
 * ECOPD-5490: 「調整出價」按鈕的顯示條件，與活動列表 (CampaignList.renderRowBtns) 一致 —
 * 依規格必須是「有出價上限 + price model 與 optimize 同種類（如 fcpm + IMPRESSIONS）」才給入口。
 *
 * priceModel 由 /campaign-dashboard/campaignList 提供；舊版後端不回這個欄位，
 * 此時一律視為不符合條件，按鈕不顯示（寧可少開入口，也不要開在規格外的活動上）。
 */
export const canManageAdminBidPrice = (
  campaign: CampaignDashboardRecord
): campaign is CampaignDashboardRecord & {
  bidPrice: number;
  priceModel: RtbCampaignPlanType;
} => {
  if (campaign.bidPrice == null || campaign.isPmp || !campaign.priceModel) {
    return false;
  }
  return defaultCampaignManager.checkOptimizeSameAsPriceModel(
    campaign.priceModel,
    campaign.optimize as L2ObjectOptimizationGoal
  );
};

/**
 * ECOPD-5490: 最佳化目標欄的顯示值。第二個參數是 price model，之前傳的是 bidPrice
 * （record 上還沒有 priceModel 時的權宜寫法），導致 fvcpm 的活動一律顯示成 CPV 而不是 vCPM，
 * 與活動列表對不起來。抽成有型別的函式，避免再被當成 any 傳錯。
 */
export const getOptimizeDisplayValue = (
  campaign: CampaignDashboardRecord
): RtbLegacyOptimize =>
  defaultCampaignManager.getLegacyOptimizeValue(
    campaign.optimize as L2ObjectOptimizationGoal,
    campaign.priceModel as RtbCampaignPlanType
  );

const defaultCampaignDashboardManager: CampaignDashboardManager =
  new DefaultCampaignDashboardManager();

const initSummary = [
  {
    label: i18n.t('campaignDashboard.labels.orderCount'),
    value: 0
  },
  {
    label: i18n.t('campaignDashboard.labels.orderTotalBudget'),
    value: 0
  },
  {
    label: i18n.t('campaignDashboard.labels.orderTotalSpent'),
    value: 0
  },
  {
    label: i18n.t('campaignDashboard.labels.campaignCount'),
    value: 0
  },
  {
    label: i18n.t('campaignDashboard.labels.impres'),
    value: 0
  },
  {
    label: i18n.t('campaignDashboard.labels.clicks'),
    value: 0
  },
  {
    label: i18n.t('campaignDashboard.labels.ctr'),
    value: '0%'
  },
  {
    label: i18n.t('campaignDashboard.labels.spent'),
    value: 0
  }
];

const getCampaignData = (campaign) => {
  const isDailyBudgetCampaign = campaign.dailyTargetBudget > 0;
  const campaignProgress = isDailyBudgetCampaign
    ? getDailyBudgetCampaignProgressRate({
        ...campaign,
        spents: campaign.spent
      })
    : getCampaignProgressRate({
        ...campaign,
        spents: campaign.spent,
        olapExpectSpent: campaign.olapExpectedSpent
      });
  // ECOPD-5476 pacing tags — computed client-side and unioned with the BE's
  // adjustmentTags. Thresholds/rounding mirror listFormatters.progressFormatter
  // (predictRate capped at 1, thresholds 0.01/0.03) so capsules match the on-screen
  // "executeRate% / predictRate%" exactly.
  const executeRate = campaignProgress.executeRate;
  const predictRate = Math.min(campaignProgress.predictRate, 1);
  const discrepancy = predictRate - executeRate;
  const daysUntilEnd = moment(campaign.endDate)
    .startOf('day')
    .diff(moment().startOf('day'), 'day');

  const adjustmentTags = new Set<string>(campaign.adjustmentTags ?? []);
  if (discrepancy > 0.03) {
    adjustmentTags.add(AdjustmentTagType.SEVERELY_BEHIND);
    // 情境C「結束前三天」：結束日 = 今天..+3 天（不含已結束的活動，daysUntilEnd < 0）。
    if (daysUntilEnd >= 0 && daysUntilEnd <= 3) {
      adjustmentTags.add(AdjustmentTagType.BUDGET_UNDERSPEND_RISK);
    }
  } else if (discrepancy > 0.01) {
    adjustmentTags.add(AdjustmentTagType.BEHIND_SCHEDULE);
  }

  return {
    ...campaign,
    [CampaignDashboardColumns.PROGRESS]: campaignProgress.executeRate,
    predictRate: campaignProgress.predictRate,
    ctr: divideValue(campaign.clicks * 100, campaign.impres),
    [CampaignDashboardColumns.OPTIMIZE]: campaign.optimize,
    adjustmentTags: Array.from(adjustmentTags)
  };
};

const addonColumnFilter = (
  columnToCheck: CampaignDashboardColumns,
  filterData: {
    column: CampaignDashboardColumns;
    addon: string;
  },
  addonFeatures: any
) => {
  const campaignAddons = addonFeatures
    ? Object.keys(addonFeatures.campaign).filter(
        (key) => addonFeatures.campaign[key]
      )
    : [];
  const hasAddon = addOnEnabled(filterData.addon).visible({
    agencyAddon: campaignAddons
  });
  return hasAddon ? true : columnToCheck !== filterData.column;
};

export const useCampaignDashboardModel = (
  agencyId: number | null | undefined,
  campaignManager: RtbCampaignManager = defaultCampaignManager,
  campaignDashboardManager: CampaignDashboardManager = defaultCampaignDashboardManager
) => {
  const campaignListFilters = useRef<{
    // serverKey = day + adType codes + campaign states; the tag filter and the
    // search string are client-side, so a change to only those re-filters the
    // already-fetched list instead of hitting the API.
    serverKey: string;
    searchString: string;
    tagKey: string;
  } | null>(null);
  const summaryInfoFilters = useRef<{
    adTypeFilter?: string;
    adTypeOptionTypeFilter?: AdTypeOptionType;
  } | null>(null);
  const [campaignList, setCampaignList] = useState<CampaignDashboardRecord[]>(
    []
  );
  const [filteredList, setFilteredList] = useState<CampaignDashboardRecord[]>(
    []
  );
  const { loading, callAPIs } = useCallAPI();
  const excelFileNameRef = useRef<string>('');
  const [campaignToChangeBidWeight, setCampaignToChangeBidWeight] = useState<
    number | string | undefined
  >();
  const [campaignToChangeAdminBidPrice, setCampaignToChangeAdminBidPrice] =
    useState<number | string | undefined>();
  const [searchString, setSearchString] = useState('');
  const [todaySummary, setTodaySummary] = useState(initSummary);
  const [yesterdaySummary, setYesterdaySummary] = useState(initSummary);
  const [days, setDays] = useState<number>(30);
  const [timeOption, setTimeOption] = useState<number>(1);
  // Multi-select filter state holds wire codes directly (rendered via
  // <Select isMulti simpleValue>, whose onChange emits the value keys).
  // Empty selection = show all. ECOPD-5414.
  const [selectedAdTypes, setSelectedAdTypes] = useState<string[]>([]);
  const [selectedCampaignStates, setSelectedCampaignStates] = useState<
    CampaignState[]
  >([CampaignState.ACTIVATE]);
  const [selectedTagCodes, setSelectedTagCodes] = useState<string[]>([]);
  const [adTypeOptions, setAdTypeOptions] = useState<AdTypeOptionItem[]>([]);
  const adTypeOptionsRef = useRef<AdTypeOptionItem[]>([]);
  const core = useCoreContext();
  const currency = get(core, 'accountManager.localeMeta.currency', 'NTD');
  const addonFeatures: any = get(
    core,
    'accountManager.localeMeta.addonFeatures'
  );

  // ---- option lists for the three multi-selects ----
  const campaignStatusOptions = useMemo<SelectOptions[]>(
    () => [
      {
        label: i18n.t('campaignList.labels.activateState'),
        value: CampaignState.ACTIVATE
      },
      {
        label: i18n.t('campaignList.labels.deactivateState'),
        value: CampaignState.DEACTIVATE
      }
    ],
    []
  );

  const adjustmentTagOptions = useMemo<SelectOptions[]>(
    () =>
      allAdjustmentTagTypes.map((tag) => ({
        // i18n keys are camelCase (sheet naming); value stays the UPPER_SNAKE
        // wire code used by the client-side filter. ECOPD-5414.
        label: i18n.t(`adjustmentTags.${camelCase(tag)}`),
        value: tag
      })),
    []
  );

  useEffect(() => {
    const fetchAdTypeOptions = async () => {
      const options = await campaignManager.getCampaignAdTypeOptions();
      adTypeOptionsRef.current = options;
      const translatedOptions: AdTypeOptionItem[] = options.map((option) => {
        const i18nKey = `adType.${String(option.value).toLowerCase()}`;
        return {
          ...option,
          label: i18n.exists(i18nKey) ? i18n.t(i18nKey) : option.label
        };
      });
      setAdTypeOptions(translatedOptions);
    };
    fetchAdTypeOptions();
  }, [campaignManager]);

  const setUpFilteredList = useCallback(
    (newCampaignList) => {
      const filteredList = newCampaignList.filter((campaign) => {
        const nameInclude = campaign.campaignName
          .toLowerCase()
          .includes(searchString.toLowerCase());
        const idInclude = campaign.campaignId.toString().includes(searchString);
        const agencyInclude = campaign.agencyName.includes(searchString);
        const advertiserInclude =
          campaign.advertiserName.includes(searchString);
        const orderInclude = campaign.orderName.includes(searchString);
        const searchMatch =
          nameInclude ||
          idInclude ||
          agencyInclude ||
          advertiserInclude ||
          orderInclude;
        // Client-side 「需要調整活動類型」filter: keep rows whose adjustmentTags
        // intersect the selected codes. Empty selection = 所有活動 = show all.
        const tagMatch =
          selectedTagCodes.length === 0 ||
          (campaign.adjustmentTags ?? []).some((tag: string) =>
            selectedTagCodes.includes(tag)
          );
        return searchMatch && tagMatch;
      });

      setFilteredList(filteredList);
    },
    [searchString, selectedTagCodes]
  );

  const getAdTypeNameById = useCallback((adTypeId: number) => {
    const option = adTypeOptionsRef.current.find(
      (opt) => String(opt.value) === String(adTypeId)
    );
    return option ? option.label : '';
  }, []);

  const fetchCampaignList = useCallback(
    (
      dayFilter: number,
      adTypes: string[],
      campaignStates: CampaignState[],
      adTypeOptionTypes: AdTypeOptionType[],
      callback?: () => void
    ) => {
      callAPIs(
        [
          () =>
            campaignDashboardManager.getCampaignList(
              dayFilter,
              adTypes,
              campaignStates,
              adTypeOptionTypes
            )
        ],
        (campaignList) => {
          const newCampaignList = campaignList.map((campaign) => {
            const adTypeName = getAdTypeNameById(campaign.adTypeId);
            return getCampaignData({
              ...campaign,
              adType: adTypeName || campaign.adType
            });
          });
          setCampaignList(newCampaignList);
          setUpFilteredList(newCampaignList);
          callback && callback();
        }
      );
    },
    [campaignDashboardManager, setUpFilteredList, callAPIs, getAdTypeNameById]
  );

  const getSummaryInfo = useCallback(
    (adTypeFilter?: string, adTypeOptionTypeFilter?: AdTypeOptionType) => {
      if (
        summaryInfoFilters.current &&
        summaryInfoFilters.current.adTypeFilter === adTypeFilter &&
        summaryInfoFilters.current.adTypeOptionTypeFilter ===
          adTypeOptionTypeFilter
      ) {
        return;
      }
      const columns = [
        'orderCount',
        'orderTotalBudget',
        'orderTotalSpent',
        'campaignCount',
        'impres',
        'clicks',
        'ctr',
        'spent'
      ];
      const formattersMap = {
        orderTotalSpent: (value) => formatPriceWithCurrency(currency, value),
        orderTotalBudget: (value) => formatPriceWithCurrency(currency, value),
        spent: (value) =>
          `${getCurrencyLabel(currency)} ${numberWithCommas(floor(value, 2))}`
      };
      const getSummaryViewData = (summary) =>
        Object.keys(summary)
          .filter((key) => key !== 'date')
          .sort((a, b) => columns.indexOf(a) - columns.indexOf(b))
          .map((key) => ({
            label: i18n.t(`campaignDashboard.labels.${key}`),
            value: formattersMap[key]
              ? formattersMap[key](summary[key])
              : numberWithCommas(summary[key])
          }));

      callAPIs(
        [
          () =>
            campaignDashboardManager.getSummaryInfo(
              adTypeFilter,
              adTypeOptionTypeFilter
            )
        ],
        (summary) => {
          setYesterdaySummary(getSummaryViewData(summary[0]));
          setTodaySummary(getSummaryViewData(summary[1]));
          summaryInfoFilters.current = {
            adTypeFilter,
            adTypeOptionTypeFilter
          };
        }
      );
    },
    [campaignDashboardManager, currency, callAPIs]
  );

  useEffect(() => {
    if (!summaryInfoFilters.current) {
      getSummaryInfo(undefined, undefined);
    }
  }, [getSummaryInfo]);

  // Per-code optionType, index-aligned with the adType codes (BE pairs them
  // positionally). Missing optionType falls back to AD_TYPE to avoid misalign.
  const resolveAdTypeOptionTypes = useCallback(
    (adTypes: string[]): AdTypeOptionType[] =>
      adTypes.map((code) => {
        const opt = adTypeOptions.find((o) => String(o.value) === code);
        return (opt?.optionType ?? 'AD_TYPE') as AdTypeOptionType;
      }),
    [adTypeOptions]
  );

  const onSearchButtonClick = useCallback(() => {
    const adTypes = selectedAdTypes;
    const adTypeOptionTypes = resolveAdTypeOptionTypes(adTypes);
    const campaignStates = selectedCampaignStates;

    const filterInfo = i18n.t(
      `campaignDashboard.fileNames.filterInfo.${timeOption}`,
      { days }
    );
    const adTypeInfo =
      adTypes.length === 0
        ? `${i18n.t('campaignDashboard.fileNames.allAdTypeInfo')}`
        : adTypes
            .map((code) => {
              const opt = adTypeOptions.find((o) => String(o.value) === code);
              return opt ? opt.label : code;
            })
            .join('_');
    const statusInfo =
      campaignStates.length === 0
        ? i18n.t('campaignDashboard.labels.allCampaigns')
        : campaignStates
            .map((state) =>
              i18n.t(`campaignDashboard.fileNames.statusInfo.${state}`)
            )
            .join('_');
    excelFileNameRef.current = `campaignDashboard_${filterInfo}_${adTypeInfo}_${statusInfo}.xlsx`;

    const dayFilter = days * timeOption;
    // Summary endpoint stays single-valued: pass the adType only when exactly
    // one is selected, otherwise show the aggregate (all).
    const summaryAdType = adTypes.length === 1 ? adTypes[0] : undefined;
    const summaryOptionType =
      adTypeOptionTypes.length === 1 ? adTypeOptionTypes[0] : undefined;

    const serverKey = JSON.stringify({
      dayFilter,
      adTypes: [...adTypes].sort(),
      campaignStates: [...campaignStates].sort()
    });
    const tagKey = JSON.stringify([...selectedTagCodes].sort());

    if (
      campaignListFilters.current &&
      campaignListFilters.current.serverKey === serverKey
    ) {
      // Only client-side filters (search / tag) changed → re-filter locally.
      if (
        campaignListFilters.current.searchString !== searchString ||
        campaignListFilters.current.tagKey !== tagKey
      ) {
        setUpFilteredList(campaignList);
        campaignListFilters.current = {
          serverKey,
          searchString,
          tagKey
        };
      }
      return;
    }

    getSummaryInfo(summaryAdType, summaryOptionType);

    fetchCampaignList(
      dayFilter,
      adTypes,
      campaignStates,
      adTypeOptionTypes,
      () => {
        campaignListFilters.current = {
          serverKey,
          searchString,
          tagKey
        };
      }
    );
  }, [
    days,
    timeOption,
    selectedAdTypes,
    selectedCampaignStates,
    selectedTagCodes,
    adTypeOptions,
    resolveAdTypeOptionTypes,
    searchString,
    campaignList,
    fetchCampaignList,
    setUpFilteredList,
    getSummaryInfo
  ]);

  const columnDefinition = (
    columnName,
    sortable = true,
    customLabel?: string
  ): ColumnDefinition => {
    const label = defaultTo(
      customLabel,
      `campaignDashboard.headers.${columnName}`
    );
    return {
      ...sortableColumn(columnName, label, sortable),
      classes: () => styles[columnName],
      headerClasses: () => styles[columnName]
    };
  };

  const refetchWithCurrentFilters = useCallback(() => {
    const dayFilter = days * timeOption;
    fetchCampaignList(
      dayFilter,
      selectedAdTypes,
      selectedCampaignStates,
      resolveAdTypeOptionTypes(selectedAdTypes)
    );
  }, [
    days,
    timeOption,
    selectedAdTypes,
    selectedCampaignStates,
    resolveAdTypeOptionTypes,
    fetchCampaignList
  ]);

  const onCampaignBidWeightUpdated = async () => {
    setCampaignToChangeBidWeight(undefined);
    refetchWithCurrentFilters();
  };

  const onCampaignAdminBidPriceUpdated = async () => {
    setCampaignToChangeAdminBidPrice(undefined);
    refetchWithCurrentFilters();
  };

  const adminColumns = [
    renderColumn(
      columnDefinition(CampaignDashboardColumns.ID),
      formatters.nameFormatter
    ),
    renderColumn(
      columnDefinition(CampaignDashboardColumns.ADJUSTMENT_TAGS, false),
      formatters.adjustmentTagsFormatter
    ),
    renderColumn(columnDefinition(CampaignDashboardColumns.AGENCY)),
    renderColumn(columnDefinition(CampaignDashboardColumns.ADVERTISER)),
    renderColumn(columnDefinition(CampaignDashboardColumns.ORDER)),
    renderColumn(
      columnDefinition(CampaignDashboardColumns.PROGRESS),
      formatters.progressFormatter
    ),
    renderColumn(
      columnDefinition(CampaignDashboardColumns.CTR),
      (value) => `${value.toFixed(2)}%`
    ),
    getLabelValueColumn(
      columnDefinition(CampaignDashboardColumns.OPTIMIZE),
      (campaign) => [
        {
          label: i18n.t('campaignDashboard.labels.optimization'),
          value: getOptimizeDisplayValue(campaign)
        },
        {
          label: i18n.t('campaignDashboard.labels.bidStrategy'),
          value: i18n.t(
            `l1Object.labels.bidStrategy.${(isNumber(campaign.bidPrice) ? BidStrategy.LOWEST_COST_WITH_BID_CAP : BidStrategy.LOWEST_COST_WITHOUT_CAP).toLowerCase()}`
          )
        },
        {
          label: i18n.t('campaignDashboard.labels.bidCap'),
          value: renderBidPriceInfo(campaign, getCurrencyLabel(currency))
        }
      ]
    ),
    renderColumn(columnDefinition(CampaignDashboardColumns.WEIGHT)),
    renderColumn(columnDefinition(CampaignDashboardColumns.START_DATE)),
    renderColumn(columnDefinition(CampaignDashboardColumns.END_DATE))
  ];

  const agencyColumns = adminColumns.filter((column) => {
    return (
      column.dataField !== CampaignDashboardColumns.AGENCY &&
      addonColumnFilter(
        column.dataField,
        {
          column: CampaignDashboardColumns.WEIGHT,
          addon: ADDONFEATURE.CAMPAIGN.BID_WEIGHT_SETTING
        },
        addonFeatures
      )
    );
  });

  const excelColumns = useMemo(
    () => [
      columnDefinition(CampaignDashboardExcelColumns.ORDER_ID),
      columnDefinition(
        CampaignDashboardExcelColumns.CAMPAIGN_ID,
        false,
        'campaignDashboard.headers.campaignId_excel'
      ),
      columnDefinition(CampaignDashboardExcelColumns.CAMPAIGN_NAME),
      columnDefinition(CampaignDashboardExcelColumns.AGENCY),
      columnDefinition(CampaignDashboardExcelColumns.START_DATE),
      columnDefinition(CampaignDashboardExcelColumns.END_DATE),
      columnDefinition(CampaignDashboardExcelColumns.BUDGET),
      columnDefinition(CampaignDashboardExcelColumns.SPENT),
      columnDefinition(
        CampaignDashboardExcelColumns.OPTIMIZE,
        false,
        'campaignDashboard.headers.optimize_excel'
      ),
      columnDefinition(CampaignDashboardExcelColumns.BID_PRICE),
      columnDefinition(CampaignDashboardExcelColumns.ADTYPE)
    ],
    []
  );

  const excelColumnDecorater = useMemo(
    () => ({
      [CampaignDashboardExcelColumns.BUDGET]: (value) =>
        formatPriceWithCurrency(currency, value),
      [CampaignDashboardExcelColumns.SPENT]: (value) =>
        formatPriceWithCurrency(currency, value),
      [CampaignDashboardExcelColumns.BID_PRICE]: (value) =>
        formatPriceWithCurrency(currency, value),
      [CampaignDashboardExcelColumns.START_DATE]: (value) =>
        moment(value).format('YYYY-MM-DD'),
      [CampaignDashboardExcelColumns.END_DATE]: (value) =>
        moment(value).format('YYYY-MM-DD'),
      [CampaignDashboardExcelColumns.ADTYPE]: (value) =>
        i18n.t(`adType.${value.toLowerCase()}`)
    }),
    [currency]
  );

  const download = useCallback(async () => {
    const getHeader = (setting) => {
      return i18n.t(setting.text);
    };
    const metricsMapI18n = excelColumns.reduce((acc, setting) => {
      acc[setting.dataField] = getHeader(setting);
      return acc;
    }, {});
    const textDecorators = excelColumns.reduce((acc, setting) => {
      acc[setting.dataField] = excelColumnDecorater[setting.dataField];
      return acc;
    }, {});
    const metrics: string[] = values(metricsMapI18n);
    const excelDataList = filteredList.reduce((excelData, rowData) => {
      const newRowData = {};
      Object.keys(rowData)
        .filter((key) => !!metricsMapI18n[key])
        .forEach((key) => {
          const textDecorator = textDecorators[key];
          newRowData[metricsMapI18n[key]] = textDecorator
            ? textDecorator(rowData[key])
            : rowData[key];
        });
      excelData.push(newRowData);
      return excelData;
    }, [] as any);
    const XLSXModule = await import('xlsx');
    const XLSX = XLSXModule.default;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelDataList, { header: metrics });
    XLSX.utils.book_append_sheet(wb, ws, 'report');
    XLSX.writeFile(wb, excelFileNameRef.current || 'campaignDashboard.xlsx');
  }, [filteredList, excelColumns, excelColumnDecorater]);

  const showManageBidWeightModal = useCallback(
    (campaignId: number | string) => {
      setCampaignToChangeBidWeight(campaignId);
    },
    []
  );

  const hideManageBidWeightModal = useCallback(() => {
    setCampaignToChangeBidWeight(undefined);
  }, []);

  const showManageAdminBidPriceModal = useCallback(
    (campaignId: number | string) => {
      setCampaignToChangeAdminBidPrice(campaignId);
    },
    []
  );

  const hideManageAdminBidPriceModal = useCallback(() => {
    setCampaignToChangeAdminBidPrice(undefined);
  }, []);

  const isPmp = useCallback(
    (campaignDashboardRecord: CampaignDashboardRecord) => {
      return campaignDashboardRecord.isPmp;
    },
    []
  );

  return {
    loading,
    days,
    searched: campaignListFilters.current !== null,
    searchString,
    timeOption,
    todaySummary,
    yesterdaySummary,
    columns: agencyId ? agencyColumns : adminColumns,
    filteredList,
    // multi-select filters (Select isMulti + simpleValue; empty = show all)
    adTypeOptions,
    campaignStatusOptions,
    adjustmentTagOptions,
    selectedAdTypes,
    selectedCampaignStates,
    selectedTagCodes,
    setSelectedAdTypes,
    setSelectedCampaignStates,
    setSelectedTagCodes,
    campaignToChangeBidWeight,
    campaignToChangeAdminBidPrice,
    isPmp,
    download,
    setTimeOption,
    setSearchString,
    setDays,
    onCampaignBidWeightUpdated,
    showManageBidWeightModal,
    hideManageBidWeightModal,
    onSearchButtonClick,
    showManageAdminBidPriceModal,
    hideManageAdminBidPriceModal,
    onCampaignAdminBidPriceUpdated
  };
};
